import React, { useState, useEffect } from 'react';
import { structureAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { 
  History, User, Building2, Briefcase, 
  Plus, Edit2, Trash2, UserPlus, UserMinus, 
  ArrowRight, RefreshCw 
} from 'lucide-react';
import { format } from 'date-fns';

const ACTION_ICONS = {
  create: Plus,
  update: Edit2,
  delete: Trash2,
  archive: Trash2,
  assign: UserPlus,
  unassign: UserMinus,
  reparent: ArrowRight,
  restore: RefreshCw
};

const ACTION_COLORS = {
  create: { bg: '#D1FAE5', text: '#065F46' },
  update: { bg: '#DBEAFE', text: '#1E40AF' },
  delete: { bg: '#FEE2E2', text: '#991B1B' },
  archive: { bg: '#FEF3C7', text: '#92400E' },
  assign: { bg: '#D1FAE5', text: '#065F46' },
  unassign: { bg: '#FEE2E2', text: '#991B1B' },
  reparent: { bg: '#E0E7FF', text: '#3730A3' },
  restore: { bg: '#D1FAE5', text: '#065F46' }
};

const ENTITY_ICONS = {
  role: Briefcase,
  department: Building2,
  person: User
};

const SettingsTab = ({ onUpdate }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAuditLogs();
  }, [filter]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { entity_type: filter } : {};
      const response = await structureAPI.getAuditLogs(params);
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const getActionLabel = (actionType) => {
    const labels = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      archive: 'Archived',
      assign: 'Assigned',
      unassign: 'Unassigned',
      reparent: 'Moved',
      restore: 'Restored'
    };
    return labels[actionType] || actionType;
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      {/* Settings Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Structure Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ color: '#264653' }}>
              Structure Settings
            </CardTitle>
            <CardDescription>
              Configure how your organization structure works
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
              <div>
                <p className="font-medium text-sm" style={{ color: '#374151' }}>Default View</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>Choose how the org chart loads by default</p>
              </div>
              <Badge variant="outline">Org Chart</Badge>
            </div>
            <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
              <div>
                <p className="font-medium text-sm" style={{ color: '#374151' }}>Show Vacant Roles</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>Display unfilled positions in the chart</p>
              </div>
              <Badge style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>Enabled</Badge>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-sm" style={{ color: '#374151' }}>Auto-collapse Levels</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>Automatically collapse nodes below a certain depth</p>
              </div>
              <Badge variant="outline" style={{ color: '#6B7280' }}>Disabled</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ color: '#264653' }}>
              Permissions
            </CardTitle>
            <CardDescription>
              Who can view and edit the organization structure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
              <div>
                <p className="font-medium text-sm" style={{ color: '#374151' }}>View Structure</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>Who can see the org chart</p>
              </div>
              <Badge style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>All Members</Badge>
            </div>
            <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
              <div>
                <p className="font-medium text-sm" style={{ color: '#374151' }}>Edit Roles</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>Who can create and modify roles</p>
              </div>
              <Badge style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>Admins Only</Badge>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-sm" style={{ color: '#374151' }}>Manage Departments</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>Who can add or remove departments</p>
              </div>
              <Badge style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>Admins Only</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit Log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2" style={{ color: '#264653' }}>
              <History className="w-5 h-5" style={{ color: '#2A9D8F' }} />
              Audit Log
            </CardTitle>
            <CardDescription>
              Track all changes made to the organization structure
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {['all', 'role', 'department', 'person'].map(type => (
              <Button
                key={type}
                variant={filter === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(type)}
                style={filter === type ? { backgroundColor: '#2A9D8F', color: '#FFFFFF' } : {}}
              >
                {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div 
                className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#2A9D8F', borderTopColor: 'transparent' }}
              />
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} />
              <p style={{ color: '#6B7280' }}>No activity recorded yet</p>
              <p className="text-sm" style={{ color: '#9CA3AF' }}>
                Changes to roles, departments, and people will appear here
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-4">
                {auditLogs.map((log, index) => {
                  const ActionIcon = ACTION_ICONS[log.actionType] || Edit2;
                  const EntityIcon = ENTITY_ICONS[log.entityType] || Briefcase;
                  const colors = ACTION_COLORS[log.actionType] || ACTION_COLORS.update;
                  
                  return (
                    <div key={log.id || index}>
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: colors.bg }}
                        >
                          <ActionIcon className="w-4 h-4" style={{ color: colors.text }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: '#374151' }}>
                              {log.actorName || 'Unknown User'}
                            </span>
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{ backgroundColor: colors.bg, color: colors.text, borderColor: 'transparent' }}
                            >
                              {getActionLabel(log.actionType)}
                            </Badge>
                            <span className="text-sm" style={{ color: '#6B7280' }}>
                              a {log.entityType}
                            </span>
                          </div>
                          
                          {/* Entity details */}
                          <div className="flex items-center gap-2 mt-1">
                            <EntityIcon className="w-3 h-3" style={{ color: '#9CA3AF' }} />
                            <span className="text-sm" style={{ color: '#6B7280' }}>
                              {log.after?.title || log.before?.title || log.after?.name || log.before?.name || 'Unknown'}
                            </span>
                          </div>
                          
                          {/* Timestamp */}
                          <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                            {formatTime(log.created_at)}
                          </p>
                        </div>
                      </div>
                      {index < auditLogs.length - 1 && (
                        <Separator className="mt-4" />
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsTab;
